import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { Disclosure, Transition } from '@headlessui/react';
import { ReactNode, useEffect, useRef } from 'react';
import clsx from 'clsx';

export const ProductPageAccordion = ({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLTableElement>(null);

  function captureHeight() {
    if (!ref.current || !innerRef.current) return;

    const { offsetHeight, clientHeight } = innerRef.current;

    ref.current.style.height = `${offsetHeight || clientHeight}px`;
  }

  function clearHeight() {
    if (!ref.current) return;
    ref.current.style.height = '';
  }

  useEffect(() => {
    const obs = new MutationObserver(captureHeight);
    if (innerRef.current) {
      obs.observe(innerRef.current, { attributes: true });
    }
    return () => {
      obs.disconnect();
    };
  }, [innerRef]);

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <Disclosure.Button
            className={clsx(
              'flex justify-between items-center',
              'w-full',
              'p-4',
              'bg-neutral-30',
              'rounded',
              'print:rounded-none',
              'print:bg-[transparent]',
              'print:p-0',
              'print:hd-base',
              'print:mb-3',
              'print:border-b-1',
              'print:pb-3'
            )}
          >
            <span>{title}</span>
            {open ? (
              <MinusIcon className={clsx('w-4', 'h-4', 'print:hidden')} />
            ) : (
              <PlusIcon className={clsx('w-4', 'h-4', 'print:hidden')} />
            )}
          </Disclosure.Button>
          <Transition
            unmount={false}
            ref={ref}
            enterFrom="!h-0"
            beforeEnter={captureHeight}
            afterEnter={clearHeight}
            leaveTo="!h-0"
            beforeLeave={captureHeight}
            afterLeave={clearHeight}
            className={clsx(
              'transition-[height]',
              'duration-300',
              'overflow-hidden',
              'print:overflow-auto print:h-max print:transition-none print:!block'
            )}
            show={open}
          >
            <Disclosure.Panel static>
              <div ref={innerRef}>{children}</div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};
